/**
 * Runs the tutorial: once the board is up, loads which steps the coach has
 * confirmed and shows the rest, one at a time. "Got it" confirms a step and
 * moves on; "Back" returns to the previous step and makes it unconfirmed again.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { TutorialStore } from '../../adapters/index.ts';
import { stepsToShow, TUTORIAL_STEPS, type TutorialStep } from './steps.ts';

export interface TutorialApi {
  /** The steps of this run, and which one is on screen; null when not showing. */
  readonly run: { readonly steps: readonly TutorialStep[]; readonly index: number } | null;
  next(): void;
  back(): void;
  /** Shows every step again, from the first — the account menu's "Show tutorial". */
  replay(): void;
}

/** Only the steps whose element is on screen right now, so a run never stalls on a missing one. */
function present(steps: readonly TutorialStep[]): readonly TutorialStep[] {
  return steps.filter((step) => document.querySelector(`[data-tutorial="${step.target}"]`) !== null);
}

export function useTutorial(store: TutorialStore, boardReady: boolean): TutorialApi {
  const [run, setRun] = useState<TutorialApi['run']>(null);
  // Saves go one after another, so a quick "Got it" then "Back" can't reach
  // the server in the wrong order and leave the step confirmed.
  const saving = useRef<Promise<void>>(Promise.resolve());
  const save = useCallback((write: () => Promise<void>) => {
    saving.current = saving.current.then(write).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!boardReady) return;
    let cancelled = false;
    store
      .confirmed()
      .then((confirmed) => {
        if (cancelled) return;
        const steps = present(stepsToShow(TUTORIAL_STEPS, confirmed));
        if (steps.length > 0) setRun({ steps, index: 0 });
      })
      // Progress that can't be loaded must not stand between a coach and the
      // board: no tutorial this time, and it is offered again next sign-in.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [store, boardReady]);

  const next = useCallback(() => {
    if (!run) return;
    const step = run.steps[run.index];
    if (step) save(() => store.confirm(step.id));
    const index = run.index + 1;
    setRun(index < run.steps.length ? { ...run, index } : null);
  }, [run, store, save]);

  const back = useCallback(() => {
    if (!run || run.index === 0) return;
    const index = run.index - 1;
    const step = run.steps[index];
    // The step gone back to is no longer confirmed until "Got it" again.
    if (step) save(() => store.unconfirm(step.id));
    setRun({ ...run, index });
  }, [run, store, save]);

  const replay = useCallback(() => {
    const steps = present(TUTORIAL_STEPS);
    if (steps.length > 0) setRun({ steps, index: 0 });
  }, []);

  return { run, next, back, replay };
}
